import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';

import ChipLabel from './ChipLabel';
import ChipIcon from './ChipIcon';
import Styled from './styles';

const ChipContext = React.createContext<any>({});

export interface InfoChipProps extends React.ComponentPropsWithRef<'div'> {
  /** `style` can be used to change the background color. Please note that only _300 library colour @ 38% transparency is recommended. For more information please refer to the "Design Detail" story. */
  style?: React.CSSProperties;
  /** The size of the InfoChip. */
  size?: 'small' | 'medium';
  /** The status of the components defining the visuals. */
  status?: 'info' | 'critical' | 'minor' | 'success' | 'non-status';
  /** The content of the chip, including `<ChipLabel />` and `<ChipIcon />` */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * Any other props than the `InfoChip` props will be provided to the root element. `size` is shared with nested elements using `ChipContext`.
 * Note: InfoChip is not meant to be used in SearchwChips, please use `<Chip />` component in a `<SearchwChips />`.
 */
const InfoChip = React.forwardRef<HTMLElement, InfoChipProps>((props: InfoChipProps, ref) => getContext(({ RTL, theme }) => {
  const {
    children,
    status,
    theme: themeProp,
    style,
    size,
    ...otherProps
  } = props;

  const resolvedTheme = _merge({}, theme.INFO_CHIP, themeProp);

  return (
    <ChipContext.Provider value={{ size, infoChipStatus: status }}>
      <Styled.InfoChip
        $theme={resolvedTheme[status]}
        ref={ref}
        RTL={RTL}
        size={size}
        style={style}
        tabIndex={0}
        {...otherProps}
      >
        {children}
      </Styled.InfoChip>
    </ChipContext.Provider>
  );
}));

InfoChip.displayName = 'InfoChip';

InfoChip.defaultProps = {
  style: {},
  size: 'medium',
  status: 'info',
  theme: {}
};

export { InfoChip as default, ChipLabel, ChipIcon, ChipContext };
